import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Button,
  FormControl,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  TextField,
} from "@material-ui/core";
import { Pagination } from "@material-ui/lab";
import { makeStyles } from "@material-ui/styles";
import AddIcon from "@material-ui/icons/Add";
import SearchIcon from "@material-ui/icons/Search";
import AlertModal from "components/AlertModal";
import { useDebounce } from "util/debounceRamda";
import { setPaginationByAmountAndSize } from "util/list-fns";
import { WidgetModal } from "./components/WidgetModal";
import { WidgetTableRow } from "./components/WidgetTableRow";
import { deleteWidget, getWidgets } from "./slice/widgetThunks";
import { selWidgetList, selWidgetTotal } from "./slice/widgetsSlice";
import { activeOptions, moduleOptions } from "./widgetConstants";

const useStyles = makeStyles({
  formControl: {
    minWidth: 140,
  },
});

const buildQuery = (params) =>
  Object.entries(params)
    .filter(([, value]) => value !== "" && value !== "all")
    .map(([key, value]) => `${key}=${encodeURIComponent(value)}`)
    .join("&");

const Widgets = () => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const widgets = useSelector(selWidgetList);
  const total = useSelector(selWidgetTotal);

  const [search, setSearch] = useState("");
  const [params, setParams] = useState({
    page: 1,
    limit: 10,
    search: "",
    module: "all",
    is_active: "all",
  });
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedWidget, setSelectedWidget] = useState(null);
  const [widgetToDelete, setWidgetToDelete] = useState(null);

  const queryParam = useMemo(() => buildQuery(params), [params]);

  const fetchWidgets = useCallback(() => dispatch(getWidgets(queryParam)), [
    dispatch,
    queryParam,
  ]);

  useEffect(() => {
    fetchWidgets();
  }, [fetchWidgets]);

  const updateParams = (value) => setParams((prev) => ({ ...prev, ...value }));

  const debouncedSearch = useDebounce(
    (value) => updateParams({ search: value, page: 1 }),
    400
  );

  function handleSearch({ target: { value } }) {
    setSearch(value);
    debouncedSearch(value.trim());
  }

  const handleFilter = (name) => ({ target: { value } }) =>
    updateParams({ [name]: value, page: 1 });

  function onClickPage(e, page) {
    updateParams({ page });
  }

  const handleOpenModal = (widget = null) => {
    setSelectedWidget(widget);
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setSelectedWidget(null);
  };

  const handleDelete = () =>
    dispatch(
      deleteWidget(widgetToDelete.id, () => {
        setWidgetToDelete(null);
        // step back if the deleted widget was the last one on this page
        if (widgets.length === 1 && params.page > 1)
          return updateParams({ page: params.page - 1 });
        return fetchWidgets();
      })
    );

  return (
    <>
      <div className="page-heading d-sm-flex flex-row justify-content-sm-between z-index-0">
        <div className="d-sm-flex flex-column justify-content-sm-between align-self-end">
          <h2 className="title">Widgets</h2>
        </div>
        <div className="d-flex flex-row align-items-end flex-wrap">
          <TextField
            className="mr-3"
            placeholder="Search by name"
            value={search}
            onChange={handleSearch}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />
          <FormControl className={`${classes.formControl} mr-3`}>
            <InputLabel id="widget-module-filter">Module</InputLabel>
            <Select
              labelId="widget-module-filter"
              value={params.module}
              onChange={handleFilter("module")}
            >
              <MenuItem value="all">All</MenuItem>
              {moduleOptions.map((item) => (
                <MenuItem key={item.value} value={item.value}>
                  {item.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl className={`${classes.formControl} mr-3`}>
            <InputLabel id="widget-status-filter">Status</InputLabel>
            <Select
              labelId="widget-status-filter"
              value={params.is_active}
              onChange={handleFilter("is_active")}
            >
              {activeOptions.map((item) => (
                <MenuItem key={item.value} value={item.value}>
                  {item.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Button
            color="primary"
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => handleOpenModal()}
          >
            Add
          </Button>
        </div>
      </div>
      <div className="jr-card">
        <div className="table-responsive-material">
          <table className="default-table table-unbordered table table-sm table-hover">
            <thead className="th-border-b">
              <tr>
                <th className="font-weight-bold">Image</th>
                <th className="font-weight-bold">Name</th>
                <th className="font-weight-bold">Module</th>
                <th className="font-weight-bold">Visualization Type</th>
                <th className="font-weight-bold">Default Height</th>
                <th className="font-weight-bold">Default Width</th>
                <th className="font-weight-bold">Permission Req</th>
                <th className="font-weight-bold">Status</th>
                <th className="font-weight-bold" style={{ textAlign: "end" }}>
                  Actions
                </th>
              </tr>
            </thead>
            <tbody>
              {widgets.map((widget) => (
                <WidgetTableRow
                  key={widget.id}
                  widget={widget}
                  onEdit={handleOpenModal}
                  onDelete={setWidgetToDelete}
                />
              ))}
              {!widgets.length && (
                <tr>
                  <td colSpan={9} className="text-center text-muted py-4">
                    No widgets found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
      <div className="w-100 d-flex justify-content-between mt-3">
        <Pagination
          page={params.page}
          count={Math.max(1, setPaginationByAmountAndSize(params.limit, total))}
          shape="rounded"
          color="primary"
          onChange={onClickPage}
        />
        <FormControl className={classes.formControl}>
          <InputLabel id="widget-page-size">Shows per page</InputLabel>
          <Select
            labelId="widget-page-size"
            value={params.limit}
            onChange={handleFilter("limit")}
          >
            <MenuItem value={5}>5</MenuItem>
            <MenuItem value={10}>10</MenuItem>
            <MenuItem value={50}>50</MenuItem>
          </Select>
        </FormControl>
      </div>
      <WidgetModal
        open={modalOpen}
        widget={selectedWidget}
        handleClose={handleCloseModal}
        onSaved={fetchWidgets}
      />
      <AlertModal
        open={!!widgetToDelete}
        handleClose={() => setWidgetToDelete(null)}
        title="Delete Widget"
        subTitle={`Are you sure want delete ${widgetToDelete?.name || ""}?`}
        onAccept={handleDelete}
      />
    </>
  );
};

export default Widgets;
